import type { Source } from '@entangled-vega/shared';

import type { GraphRelationship } from '../data/graph-types.js';
import type { GraphIndex } from './graph.js';

const SUPPLEMENTAL_STATUSES = new Set<GraphRelationship['status']>([
  'reported',
  'rumored',
  'disputed',
]);

export function isSupplementalRelationship(relationship: GraphRelationship): boolean {
  return SUPPLEMENTAL_STATUSES.has(relationship.status);
}

export function relationshipProvenanceLabel(relationship: GraphRelationship): string {
  switch (relationship.status) {
    case 'wikidata':
      return 'Wikidata';
    case 'reported':
      return 'Reported';
    case 'rumored':
      return 'Rumored';
    case 'disputed':
      return 'Disputed';
    case 'documented':
      return 'Wikidata';
  }
}

const SOURCE_PRIORITY: Readonly<Record<Source['sourceClass'], number>> = {
  official: 0,
  authority: 1,
  'reputable-publication': 2,
  reference: 3,
  wikidata: 4,
  unclassified: 5,
};

export function relationshipTone(relationship: GraphRelationship): string {
  if (isSupplementalRelationship(relationship)) {
    return 'reported';
  }
  return relationship.kind === 'spouse' ? 'spouse' : relationship.kind;
}

export function sourceDisplayLabel(source: Source): string {
  if (source.sourceClass === 'wikidata') {
    return 'Wikidata statement';
  }
  if (source.publisher !== undefined) {
    return source.publisher;
  }
  if (source.title !== undefined && !source.title.startsWith('Wikidata source item')) {
    return source.title;
  }
  try {
    return new URL(source.url).hostname.replace(/^www\./u, '');
  } catch {
    return 'Source';
  }
}

export function sourcesForRelationship(
  relationship: GraphRelationship,
  index: GraphIndex,
): readonly Source[] {
  return (relationship.sourceIds ?? [])
    .map((sourceId) => index.sourcesById.get(sourceId))
    .filter((source): source is Source => source !== undefined)
    .sort(
      (left, right) =>
        SOURCE_PRIORITY[left.sourceClass] - SOURCE_PRIORITY[right.sourceClass] ||
        sourceDisplayLabel(left).localeCompare(sourceDisplayLabel(right)) ||
        left.id.localeCompare(right.id),
    );
}

export type SourceDisplayGroup = {
  label: string;
  links: readonly {
    id: string;
    label: string;
    url: string;
    sourceClass: Source['sourceClass'];
  }[];
};

function sourceGroupLabel(source: Source): string {
  let hostname = '';
  try {
    hostname = new URL(source.url).hostname.replace(/^www\./u, '');
  } catch {
    // Source URLs are schema-validated; retain a stable fallback for fixtures.
  }
  if (source.sourceClass === 'wikidata' || hostname === 'wikidata.org') {
    return 'Wikidata';
  }
  if (hostname === 'wikipedia.org' || hostname.endsWith('.wikipedia.org')) {
    return 'Wikipedia';
  }
  return source.publisher ?? (hostname || 'Other');
}

export function groupSourcesForDisplay(sources: readonly Source[]): SourceDisplayGroup[] {
  const groups = new Map<string, Source[]>();
  const seenUrls = new Set<string>();
  for (const source of sources) {
    if (seenUrls.has(source.url)) {
      continue;
    }
    seenUrls.add(source.url);
    const label = sourceGroupLabel(source);
    const group = groups.get(label) ?? [];
    group.push(source);
    groups.set(label, group);
  }
  return [...groups].map(([label, groupedSources]) => ({
    label,
    links: groupedSources.map((source, index) => {
      const genericLabel =
        label === 'Wikidata'
          ? 'Statement'
          : label === 'Wikipedia'
            ? 'Article revision'
            : sourceDisplayLabel(source);
      return {
        id: source.id,
        label: groupedSources.length > 1 ? `${genericLabel} ${index + 1}` : genericLabel,
        url: source.url,
        sourceClass: source.sourceClass,
      };
    }),
  }));
}
